import { useEffect, useMemo, useState } from "react";
import { useDiscoveryRequest } from "../../../contexts/DiscoveryRequestContext";
import { Button } from "../../../design/button";
import {
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../../design/dialog";
import { Table, TableCell, TableRow } from "../../../design/table";
import LoadingView from "../../../components/LoadingView";
import { DiscoveryRequest } from "../../../models/DiscoveryRequest";
import { Checkbox } from "../../../design/checkbox";

interface Props {
  discoveryRequestId: DiscoveryRequest["_id"];
  onCancel: () => void;
}

const RecommendedTasksComponent: React.FC<Props> = ({
  discoveryRequestId,
  onCancel,
}) => {
  const { getRecommendedTasks, insertTasks, loading } = useDiscoveryRequest();
  const [recommendedTasks, setRecommendedTasks] = useState<string[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchRecommendedTasks = async () => {
      try {
        const tasks = await getRecommendedTasks(discoveryRequestId);
        setRecommendedTasks(tasks || []);
        setSelectedTasks(tasks || []); // Pre-select all by default
      } catch (error) {
        console.error("Failed to fetch recommended tasks:", error);
      }
    };

    fetchRecommendedTasks();
  }, [discoveryRequestId]);

  const toggleTask = (task: string) => {
    setSelectedTasks((prev) =>
      prev.includes(task) ? prev.filter((t) => t !== task) : [...prev, task],
    );
  };

  const taskRows = useMemo(
    () =>
      recommendedTasks.map((task) => (
        <TableRow key={task}>
          <TableCell>
            <Checkbox
              id={`task-${task}`}
              name={`task-${task}`}
              value={task}
              checked={selectedTasks.includes(task)}
              onChange={() => toggleTask(task)}
            />
          </TableCell>
          <TableCell style={{ whiteSpace: "pre-line" }}>{task}</TableCell>
        </TableRow>
      )),
    [recommendedTasks, selectedTasks],
  );

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await insertTasks(discoveryRequestId!, selectedTasks);
      onCancel(); // Close the dialog after saving
    } catch (e) {
      console.error("Error saving tasks:", e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <DialogTitle>Recommended Tasks</DialogTitle>
      {!loading.recommendedTasks && (
        <DialogDescription>
          Please review the recommended tasks below. You can select the ones you
          want to save to your discovery request.
        </DialogDescription>
      )}
      <DialogBody>
        {loading.recommendedTasks ? (
          <LoadingView title="Loading Recommended Tasks" />
        ) : recommendedTasks.length === 0 ? (
          <p>No recommended tasks available.</p>
        ) : (
          <Table>{taskRows}</Table>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={isSaving || selectedTasks.length === 0}
          onClick={handleSubmit}
        >
          {isSaving ? "Saving..." : `Create ${selectedTasks.length} Task(s)`}
        </Button>
      </DialogActions>
    </>
  );
};

export default RecommendedTasksComponent;
